import type { Item, Tag, Invite, CalendarImportSource, GoogleCalendarCandidate } from '@chaos-concierge/shared';
import type { CreateItemInput, DeviceReminder, ItemPatch, PlannerStore, PushSubscriptionPayload, SpaceMember, SpaceRole, SpaceWithMembers } from './types';

const API = import.meta.env.VITE_API_URL as string;

export class ApiStore implements PlannerStore {
  constructor(private getToken: () => Promise<string>) {}

  private async headers(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);
    try {
      const res = await fetch(`${API}${path}`, {
        method,
        headers: await this.headers(),
        body: body !== undefined ? JSON.stringify(body) : undefined,
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${method} ${path} → ${res.status}: ${text}`);
      }
      return res.json() as Promise<T>;
    } finally {
      clearTimeout(timeout);
    }
  }

  list(): Promise<Item[]> {
    return this.request('GET', 'items');
  }

  create(input: CreateItemInput): Promise<Item> {
    return this.request('POST', 'items', input);
  }

  update(id: string, patch: ItemPatch): Promise<Item> {
    return this.request('PATCH', `items/${id}`, patch);
  }

  async remove(id: string): Promise<void> {
    await this.request('DELETE', `items/${id}`);
  }

  listTags(): Promise<Tag[]> {
    return this.request('GET', 'labels');
  }

  async createTag(name: string, color: string): Promise<Tag> {
    return this.request('POST', 'labels', { name, color });
  }

  async updateTag(id: string, patch: Partial<Pick<Tag, 'name' | 'color'>>): Promise<Tag> {
    return this.request('PATCH', `labels/${id}`, patch);
  }

  async removeTag(id: string): Promise<void> {
    await this.request('DELETE', `labels/${id}`);
  }

  // ── shared spaces ──────────────────────────────────────────────────────────

  listSpaces(): Promise<SpaceWithMembers[]> {
    return this.request('GET', 'spaces');
  }

  createSpace(name: string, color: string): Promise<SpaceWithMembers> {
    return this.request('POST', 'spaces', { name, color });
  }

  async deleteSpace(spaceId: string): Promise<void> {
    await this.request('DELETE', `spaces/${spaceId}`);
  }

  async leaveSpace(spaceId: string): Promise<void> {
    await this.request('POST', `spaces/${spaceId}/leave`);
  }

  inviteToSpace(spaceId: string, email: string): Promise<Invite> {
    return this.request('POST', `spaces/${spaceId}/invites`, { email });
  }

  async revokeSpaceInvite(spaceId: string, email: string): Promise<void> {
    await this.request('DELETE', `spaces/${spaceId}/invites/${encodeURIComponent(email)}`);
  }

  async removeSpaceMember(spaceId: string, userId: string): Promise<void> {
    await this.request('DELETE', `spaces/${spaceId}/members/${encodeURIComponent(userId)}`);
  }

  updateSpaceMemberRole(spaceId: string, userId: string, role: Exclude<SpaceRole, 'owner'>): Promise<SpaceMember> {
    return this.request('PATCH', `spaces/${spaceId}/members/${encodeURIComponent(userId)}`, { role });
  }

  listInvites(): Promise<Invite[]> {
    return this.request('GET', 'invites');
  }

  acceptInvite(spaceId: string): Promise<SpaceWithMembers> {
    return this.request('POST', `invites/${spaceId}/accept`);
  }

  async declineInvite(spaceId: string): Promise<void> {
    await this.request('POST', `invites/${spaceId}/decline`);
  }

  listCalendarImports(): Promise<CalendarImportSource[]> {
    return this.request('GET', 'calendar-imports');
  }

  startGoogleCalendarConnect(): Promise<{ authUrl: string }> {
    return this.request('POST', 'calendar-imports/google/connect');
  }

  finishGoogleCalendarConnect(code: string, state: string): Promise<{ accountEmail: string; calendars: GoogleCalendarCandidate[] }> {
    return this.request('POST', 'calendar-imports/google/callback', { code, state });
  }

  listGoogleCalendarCandidates(): Promise<GoogleCalendarCandidate[]> {
    return this.request('GET', 'calendar-imports/google/calendars');
  }

  async disconnectGoogleCalendarAccount(accountEmail: string): Promise<void> {
    await this.request('DELETE', `calendar-imports/google/accounts/${encodeURIComponent(accountEmail)}`);
  }

  createGoogleCalendarImport(input: Pick<GoogleCalendarCandidate, 'accountEmail' | 'calendarId'>): Promise<CalendarImportSource> {
    return this.request('POST', 'calendar-imports/google/import', input);
  }

  updateCalendarImport(importId: string, patch: Partial<Pick<CalendarImportSource, 'privacy' | 'includeDeclined' | 'includeAllDay'>>): Promise<CalendarImportSource> {
    return this.request('PATCH', `calendar-imports/${importId}`, patch);
  }

  syncCalendarImport(importId: string): Promise<CalendarImportSource> {
    return this.request('POST', `calendar-imports/${importId}/sync`);
  }

  async removeCalendarImport(importId: string): Promise<void> {
    await this.request('DELETE', `calendar-imports/${importId}`);
  }

  getPushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> {
    return this.request('GET', 'push/config');
  }

  savePushSubscription(input: { subscription: PushSubscriptionPayload; deviceId: string; timezone: string; userAgent: string }): Promise<{ subscribed: boolean }> {
    return this.request('POST', 'push/subscriptions', input);
  }

  removePushSubscription(endpoint: string): Promise<{ subscribed: boolean }> {
    return this.request('DELETE', 'push/subscriptions', { endpoint });
  }

  listDeviceReminders(deviceId: string): Promise<DeviceReminder[]> {
    return this.request('GET', `device-reminders?deviceId=${encodeURIComponent(deviceId)}`);
  }

  setDeviceReminder(deviceId: string, itemId: string, reminder: string | null): Promise<DeviceReminder> {
    return this.request('PATCH', `device-reminders/${encodeURIComponent(itemId)}`, { deviceId, reminder });
  }
}
